import { useState, useEffect } from 'react';
import axios from 'axios';
import { Check, Minus, ChevronDown, ChevronRight, AlertTriangle, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { SOP_STAGE_LABELS } from '@/lib/sopConstants';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TOGGLE_CYCLE = { pending: 'complete', complete: 'na', na: 'pending' };
const STAGE_ORDER = ['00', '01', '02', '03', '04'];

const CheckpointPanel = ({ projectId }) => {
  const [checkpoints, setCheckpoints] = useState([]);
  const [milestones, setMilestones] = useState({});
  const [loading, setLoading] = useState(true);
  const [collapsed, setCollapsed] = useState({});
  const [confirmSignOff, setConfirmSignOff] = useState(null);
  const [signingOff, setSigningOff] = useState(null);

  const fetchCheckpoints = async () => {
    const res = await axios.get(`${API}/projects/${projectId}/checkpoints`, { withCredentials: true });
    if (res.data.length === 0) {
      await axios.post(`${API}/projects/${projectId}/checkpoints/init`, {}, { withCredentials: true });
      const res2 = await axios.get(`${API}/projects/${projectId}/checkpoints`, { withCredentials: true });
      setCheckpoints(res2.data);
    } else {
      setCheckpoints(res.data);
    }
  };

  const fetchMilestones = async () => {
    try {
      const res = await axios.get(`${API}/projects/${projectId}/milestones`, { withCredentials: true });
      const dict = {};
      (Array.isArray(res.data) ? res.data : []).forEach(m => { dict[m.stage] = m; });
      setMilestones(dict);
    } catch {
      // milestones endpoint may not be present yet — not fatal, start empty
    }
  };

  useEffect(() => {
    if (!projectId) return;
    setLoading(true);
    Promise.all([
      fetchCheckpoints().catch(() => toast.error('Failed to load checkpoints')),
      fetchMilestones(),
    ]).finally(() => setLoading(false));
  }, [projectId]);

  // Group checkpoints by stage
  const grouped = STAGE_ORDER.reduce((acc, stage) => {
    acc[stage] = checkpoints.filter(cp => cp.stage === stage);
    return acc;
  }, {});

  const stageStats = (stage) => {
    const cps = grouped[stage];
    const done = cps.filter(cp => cp.status === 'complete' || cp.status === 'na').length;
    return { total: cps.length, done };
  };

  const isStageAllDone = (stage) =>
    grouped[stage].length > 0 &&
    grouped[stage].every(cp => cp.status === 'complete' || cp.status === 'na');

  const hasAmberWarning = (stage) => {
    const idx = STAGE_ORDER.indexOf(stage);
    if (idx >= STAGE_ORDER.length - 1) return false;
    const nextStage = STAGE_ORDER[idx + 1];
    const stageNotSignedOff = !milestones[stage]?.signed_off_at;
    const nextHasCompleted = (grouped[nextStage] || []).some(cp => cp.status === 'complete');
    return stageNotSignedOff && nextHasCompleted;
  };

  const toggleCheckpoint = async (cp) => {
    const newStatus = TOGGLE_CYCLE[cp.status] || 'complete';
    const original = cp.status;
    // Optimistic update
    setCheckpoints(prev => prev.map(c => c.id === cp.id ? { ...c, status: newStatus } : c));
    try {
      const res = await axios.patch(
        `${API}/projects/${projectId}/checkpoints/${cp.id}`,
        { status: newStatus },
        { withCredentials: true }
      );
      setCheckpoints(prev => prev.map(c => c.id === cp.id ? res.data : c));
    } catch {
      // Revert on failure
      setCheckpoints(prev => prev.map(c => c.id === cp.id ? { ...c, status: original } : c));
      toast.error('Failed to update checkpoint');
    }
  };

  const handleSignOff = async (stage) => {
    setSigningOff(stage);
    try {
      const res = await axios.post(
        `${API}/projects/${projectId}/milestones/${stage}/sign-off`,
        {},
        { withCredentials: true }
      );
      setMilestones(prev => ({ ...prev, [stage]: res.data }));
      setConfirmSignOff(null);
      toast.success(`Stage ${stage} signed off`);
    } catch {
      toast.error('Sign-off failed');
    } finally {
      setSigningOff(null);
    }
  };

  const fmtDate = (iso) =>
    new Date(iso).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });

  const fmtShort = (iso) =>
    new Date(iso).toLocaleDateString('en-AU', { day: 'numeric', month: 'short' });

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center">
        <Loader2 className="w-6 h-6 text-primary animate-spin" />
      </div>
    );
  }

  return (
    <div className="h-full overflow-auto p-6" data-testid="checkpoint-panel">
      <div className="max-w-4xl mx-auto">
        <div>
          <div className="mb-6">
            <h2 className="font-heading text-2xl text-foreground mb-2">Project Status</h2>
            <p className="text-sm text-muted-foreground font-mono">
              Stage checkpoints — track completion and milestone sign-offs
            </p>
          </div>

          <div className="space-y-4">
            {STAGE_ORDER.map(stage => {
              const info = SOP_STAGE_LABELS[stage];
              const { total, done } = stageStats(stage);
              const allDone = isStageAllDone(stage);
              const milestone = milestones[stage];
              const isCollapsed = collapsed[stage];
              const amber = hasAmberWarning(stage);

              return (
                <div key={stage} className="glass border border-border">
                  {/* Stage header row */}
                  <div
                    className="flex items-center justify-between px-5 py-4 cursor-pointer hover:bg-black/[0.02] transition-colors select-none"
                    onClick={() => setCollapsed(prev => ({ ...prev, [stage]: !prev[stage] }))}
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <span className="text-muted-foreground flex-shrink-0">
                        {isCollapsed
                          ? <ChevronRight className="w-4 h-4" />
                          : <ChevronDown className="w-4 h-4" />}
                      </span>
                      <span
                        className="font-mono text-xs font-semibold px-2 py-1 text-white flex-shrink-0"
                        style={{ backgroundColor: info.color, borderRadius: 0 }}
                      >
                        {stage}
                      </span>
                      <div className="min-w-0">
                        <span className="font-heading text-base text-foreground">{info.label}</span>
                        <span className="text-xs text-muted-foreground font-mono ml-2 hidden sm:inline">
                          · {info.duration}
                        </span>
                      </div>
                    </div>

                    <div className="flex items-center gap-3 flex-shrink-0 ml-4" onClick={e => e.stopPropagation()}>
                      <span className="font-mono text-xs text-muted-foreground">{done}/{total} complete</span>

                      {milestone?.signed_off_at ? (
                        <span
                          className="font-mono text-xs text-green-700 bg-green-50 border border-green-300 px-2 py-1 whitespace-nowrap"
                          style={{ borderRadius: 0 }}
                        >
                          SIGNED OFF {fmtDate(milestone.signed_off_at)}
                        </span>
                      ) : allDone ? (
                        confirmSignOff === stage ? (
                          <div className="flex items-center gap-2">
                            <span className="font-mono text-xs text-muted-foreground hidden md:inline">
                              Sign off Stage {stage}? This records your approval.
                            </span>
                            <Button
                              size="sm"
                              onClick={() => handleSignOff(stage)}
                              disabled={signingOff === stage}
                              className="bg-primary text-primary-foreground font-mono text-xs h-7 px-3 hover:bg-primary/90"
                              style={{ borderRadius: 0 }}
                            >
                              {signingOff === stage
                                ? <Loader2 className="w-3 h-3 animate-spin" />
                                : 'Confirm'}
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => setConfirmSignOff(null)}
                              className="font-mono text-xs h-7 px-3 text-muted-foreground hover:text-foreground"
                              style={{ borderRadius: 0 }}
                            >
                              Cancel
                            </Button>
                          </div>
                        ) : (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setConfirmSignOff(stage)}
                            className="font-mono text-xs h-7 px-3 bg-black/[0.03] border-border text-foreground hover:bg-black/[0.06] whitespace-nowrap"
                            style={{ borderRadius: 0 }}
                          >
                            Sign Off {stage}
                          </Button>
                        )
                      ) : null}
                    </div>
                  </div>

                  {/* Amber warning — not a lock, just a signal */}
                  {amber && (
                    <div
                      className="mx-5 mb-3 flex items-start gap-2 bg-amber-50 border border-amber-300 px-4 py-2.5"
                      style={{ borderRadius: 0 }}
                    >
                      <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                      <span className="font-mono text-xs text-amber-800">
                        Stage {stage} has incomplete checkpoints — review before proceeding
                      </span>
                    </div>
                  )}

                  {/* Checkpoint rows */}
                  {!isCollapsed && (
                    <div className="border-t border-border">
                      {grouped[stage].length === 0 ? (
                        <p className="px-5 py-4 text-sm text-muted-foreground font-mono">
                          No checkpoints for this stage.
                        </p>
                      ) : (
                        grouped[stage].map((cp, idx) => (
                          <div
                            key={cp.id}
                            className={`flex items-center gap-4 px-5 py-3 ${
                              idx < grouped[stage].length - 1 ? 'border-b border-border' : ''
                            }`}
                          >
                            {/* Square toggle checkbox — pending / complete / na cycle */}
                            <button
                              onClick={() => toggleCheckpoint(cp)}
                              title={`Status: ${cp.status} — click to cycle`}
                              className="w-5 h-5 border-2 flex items-center justify-center flex-shrink-0 transition-colors"
                              style={{
                                borderRadius: 0,
                                borderColor: cp.status === 'complete' ? '#C5A059' : cp.status === 'na' ? 'rgba(27,42,61,0.25)' : 'rgba(27,42,61,0.4)',
                                backgroundColor: cp.status === 'complete' ? '#C5A059' : 'transparent',
                              }}
                            >
                              {cp.status === 'complete' && <Check className="w-3 h-3 text-white" />}
                              {cp.status === 'na' && <Minus className="w-3 h-3 text-muted-foreground" />}
                            </button>

                            <span
                              className={`flex-1 text-sm ${
                                cp.status === 'complete'
                                  ? 'line-through text-muted-foreground'
                                  : cp.status === 'na'
                                  ? 'text-muted-foreground/60 italic'
                                  : 'text-foreground'
                              }`}
                            >
                              {cp.label}
                            </span>

                            {cp.owner_role && (
                              <span
                                className="font-mono text-xs bg-primary/10 text-primary px-2 py-0.5 flex-shrink-0"
                                style={{ borderRadius: 0 }}
                              >
                                {cp.owner_role}
                              </span>
                            )}

                            {cp.completed_at && cp.status === 'complete' && (
                              <span className="font-mono text-xs text-muted-foreground flex-shrink-0 hidden sm:block">
                                {fmtShort(cp.completed_at)}
                              </span>
                            )}
                          </div>
                        ))
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CheckpointPanel;
